import { z } from "zod";
export const ApprovalRequiredSchema = z.object({
    code: z.literal("APPROVAL_REQUIRED"),
    requestId: z.string(),
    message: z.string(),
});
/**
 * `POST /v1/rooms/join` 403 body for a password-checked room when the request
 * carried no `passwordProof` or a wrong one (the client knows which). `roomId`
 * is the room the request resolved to: a name-slug match can redirect the join,
 * and the proof is bound to the real id.
 */
export const RoomPasswordInvalidSchema = z.object({
    code: z.literal("ROOM_PASSWORD_INVALID"),
    message: z.string(),
    roomId: z.string(),
});
export const RoomErrorSchema = z.object({
    code: z.enum([
        "ROOM_NOT_FOUND",
        "USER_BANNED",
        "APPROVAL_REQUIRED",
        "APP_TRIAL_EXPIRED",
        "ROOM_PASSWORD_INVALID",
        "ROOM_PASSWORD_FIXED",
        "ROOM_ID_TAKEN",
    ]).optional(),
    message: z.string().optional(),
    bannedUntil: z.number().optional(),
    requestId: z.string().optional(),
    roomId: z.string().optional(),
});
/**
 * One-way proof that the caller holds a room's end-to-end encryption password,
 * derived on the client from the exact stored password:
 * `PBKDF2-HMAC-SHA256(password, "andor/room-access/v1/" + roomId, 600_000
 * iterations, 256 bits)` as unpadded base64url. The label keeps it independent
 * of the media keys the cipher uses (`RoomMediaKeySchema`), so the server never
 * learns anything that decrypts traffic. The server stores only a hash of
 * `verifier`.
 */
export const RoomPasswordProofSchema = z.object({
    v: z.literal(1),
    verifier: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
});
/**
 * A client-chosen id for a new room: a lowercase UUID as `crypto.randomUUID()`
 * produces, which `normalizeRoomId` leaves unchanged. Clients choose it so they
 * can derive the `passwordProof` (salted with the id) before the room exists.
 */
export const ClientRoomIdSchema = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/);
/**
 * How a room's schedule repeats. `frequency` is the calendar unit and
 * `interval` is how many of those units between occurrences ("every 2nd week"
 * is `{ frequency: "weekly", interval: 2 }`). A UI slider can snap to the common
 * presets (daily=`daily/1`, weekly=`weekly/1`, biweekly=`weekly/2`,
 * monthly=`monthly/1`) while `interval` stays open-ended for finer control.
 */
export const RecurrenceSchema = z.object({
    frequency: z.enum(["daily", "weekly", "monthly"]),
    interval: z.number().int().positive().max(365).default(1),
});
/** Whether `zone` is an IANA time zone this runtime's `Intl` knows. */
function isTimeZone(zone) {
    try {
        new Intl.DateTimeFormat("en-US", { timeZone: zone });
        return true;
    }
    catch {
        return false;
    }
}
/**
 * A room's single schedule. `startTime`/`endTime` are absolute epoch-ms instants
 * bounding the (first) occurrence window. An absent `recurrence` means a one-off
 * window; a present one repeats that window on the given cadence indefinitely.
 * `timeZone` is the IANA zone whose wall clock recurrences keep, so a weekly
 * 10:00 stays at 10:00 across daylight saving changes; schedules saved without
 * one keep the legacy fixed-length cadence. Evaluated by `./schedule.ts`.
 */
export const RoomScheduleSchema = z.object({
    startTime: z.number().int().nonnegative(),
    endTime: z.number().int().nonnegative(),
    recurrence: RecurrenceSchema.optional(),
    timeZone: z.string().min(1).max(64).refine(isTimeZone, {
        message: "timeZone must be an IANA time zone",
    }).optional(),
}).refine((s) => s.endTime > s.startTime, {
    message: "endTime must be after startTime",
});
export const JoinRoomResponseSchema = z.object({
    kid: z.string(),
    token: z.string(),
    roomId: z.string(),
    name: z.string().optional(),
    isPrivate: z.boolean().optional(),
    hasPassword: z.boolean().optional(),
    isHidden: z.boolean().optional(),
    schedule: RoomScheduleSchema.optional(),
    /**
     * A password room's current media key epoch (see `RoomMediaKeySchema`);
     * absent means 0. Members follow later rotations via `room-media-key`.
     */
    mediaKeyEpoch: z.number().int().min(0).optional(),
});
/**
 * `POST /v1/rooms/create` body. Clients may choose the new room's `roomId`
 * (needed to derive a `passwordProof`); without one the server mints it. A
 * taken id is refused with 409 `ROOM_ID_TAKEN`. Either way the response carries
 * the room's id. `roomName` is the first piece of room metadata; the E2EE mode
 * (`hasPassword`) and its password are fixed at creation.
 */
export const CreateRoomRequestSchema = z.object({
    roomName: z.string().min(1).max(256),
    isPrivate: z.boolean().default(false),
    hasPassword: z.boolean().default(false),
    /** Create the room hidden (excluded from the room list). See RoomMetadata. */
    isHidden: z.boolean().default(false),
    /** Optional single schedule attached at creation; omit for no schedule. */
    schedule: RoomScheduleSchema.optional(),
    roomId: ClientRoomIdSchema.optional(),
    /**
     * Makes joins require the room password (see {@link RoomPasswordProofSchema});
     * implies `hasPassword`. Rooms created without one are not password-checked.
     */
    passwordProof: RoomPasswordProofSchema.optional(),
});
/**
 * `POST /v1/rooms/join` body. `roomId` may be a server-minted UUID (a durable
 * room opened via `POST /v1/rooms/create`) or a client-supplied slug/invite-link
 * id — the handler normalizes it (`normalizeRoomId`). `userName` is the
 * requester's display name — used only to label them to moderators when the join
 * requires approval (the knock-to-join flow). Guests have no server-side name, so
 * the client supplies it (same convention as the kick flow); it falls back to the
 * andorUUID when absent.
 *
 * `createIfMissing` opts the caller into open-on-demand: when the room does not
 * exist it is created as a **hidden, non-private (instant) room** and joined in
 * one call, so old invite links and typed room names resolve to a room instead
 * of 404ing. Only callers holding `manageRooms` may open a room this way; for
 * everyone else the flag is ignored. `roomName` and `hasPassword` seed that new
 * room's display name and
 * E2EE mode and apply **only** when a room is created here — for an existing room
 * the stored metadata always wins.
 *
 * `passwordProof` proves the caller holds the room's E2EE password. A
 * password-checked room refuses a missing or wrong proof with 403
 * `ROOM_PASSWORD_INVALID` — privileged callers included — before any knock or
 * grant. On open-on-demand it also makes the new room password-checked.
 */
export const JoinRoomRequestSchema = z.object({
    roomId: z.string().min(1).max(256),
    userName: z.string().max(256).optional(),
    createIfMissing: z.boolean().default(false),
    roomName: z.string().min(1).max(256).optional(),
    hasPassword: z.boolean().default(false),
    passwordProof: RoomPasswordProofSchema.optional(),
});
/**
 * `POST /v1/rooms/update` body. A partial update of a room's descriptive
 * metadata: every field except `roomId` is optional and only the provided ones
 * change. `schedule` is tri-state — omit it to leave the schedule unchanged,
 * send `null` to clear it, or send an object to set it. `createdAt`/`createdBy`
 * are never updatable.
 */
export const UpdateRoomRequestSchema = z.object({
    roomId: z.string().min(1).max(256),
    name: z.string().min(1).max(256).optional(),
    isPrivate: z.boolean().optional(),
    /**
     * The E2EE mode is fixed at creation: a value differing from the stored one is
     * refused with 400 `ROOM_PASSWORD_FIXED`; an equal value is a no-op. To change
     * or remove a password, create a new room.
     */
    hasPassword: z.boolean().optional(),
    /**
     * Flip a room's visibility. Setting `false` reveals a previously hidden room
     * to the domain (broadcast as `room-added`); setting `true` hides it
     * (broadcast as `room-closed`). See `updateRoomEntry`.
     */
    isHidden: z.boolean().optional(),
    schedule: z.union([RoomScheduleSchema, z.null()]).optional(),
});
/**
 * `POST /v1/rooms/join/respond` body. A moderator's decision on a pending
 * knock-to-join request, identified by the `requestId` carried in the
 * `join-request` WS message. `granted: true` admits the user (mints a
 * short-lived pass they consume on their next join); `false` denies them.
 */
export const JoinRequestResponseSchema = z.object({
    requestId: z.string().min(1).max(256),
    granted: z.boolean(),
});
/**
 * `POST /v1/rooms/join/cancel` body. The requester withdrawing their own pending
 * knock (e.g. they pressed Cancel on the waiting overlay), identified by the
 * `requestId` from the 202 `APPROVAL_REQUIRED` response. Ownership is enforced
 * server-side: a user can only cancel a request they themselves created.
 */
export const JoinRequestCancelSchema = z.object({
    requestId: z.string().min(1).max(256),
});
/**
 * `POST /v1/rooms/close` body. Closing is by `roomId` and permanently removes
 * the room from KV.
 */
export const CloseRoomRequestSchema = z.object({
    roomId: z.string().min(1).max(256),
});
/**
 * Public room shape used in broadcast payloads (`room-added`, `room-updated`)
 * and the `rooms-snapshot`. It is the durable `RoomMetadata` projected for the
 * wire plus `memberCount`, the live number of members currently attached to
 * the room (sourced from in-memory presence; `0` when nobody is attached).
 * Both `isPrivate` and `hasPassword` default to false (public, passwordless)
 * when absent on the wire.
 */
/**
 * Live, ownership-carrying state of an active recording for a room. Present on
 * a `RoomDescriptor` (below) only while a recording is running; absent/null
 * otherwise. This is the **public** projection shown to clients — it names the
 * user who started the recording (so the UI can gate the Stop button to them)
 * but deliberately omits the Cortex `sessionId`, which is server-only
 * (`RecordingRecordSchema`). Like `memberCount`, it is transient/live state,
 * NOT durable `RoomMetadata`; it lives in KV under `[domain, "recording",
 * roomId]` and is merged into the descriptor when built.
 */
export const RecordingStateSchema = z.object({
    /** andorUUID of the user who started the recording — the only one allowed to stop it. */
    startedBy: z.string(),
    /** Display name of the starter, for the "Recording started by …" UI. */
    startedByName: z.string(),
    /** Epoch ms the recording was started. */
    startedAt: z.number(),
});
export const RoomDescriptorSchema = z.object({
    roomId: z.string(),
    name: z.string(),
    isPrivate: z.boolean().default(false),
    hasPassword: z.boolean().default(false),
    /** Mirrors `RoomMetadata.isHidden`. Sent on `GET /v1/rooms/:roomId` so a
     * client resolving a room by id (e.g. an invite link) can keep it out of its
     * own browse list. Hidden rooms never appear in the snapshot/deltas. */
    isHidden: z.boolean().default(false),
    memberCount: z.number().int().nonnegative().default(0),
    /** The room's schedule, when it has one (mirrors `RoomMetadata.schedule`). */
    schedule: RoomScheduleSchema.optional(),
    /** Live recording state (owner + start time), or null/absent when the room is
     * not being recorded. Merged in from the recording KV record when the
     * descriptor is built (see `roomRegistry.descriptorOf`), so every
     * `room-updated`/snapshot carries the current value. */
    recording: z.union([RecordingStateSchema, z.null()]).optional(),
});
/** `GET /v1/rooms` response — the on-demand equivalent of `rooms-snapshot`. */
export const ListRoomsResponseSchema = z.object({
    rooms: z.array(RoomDescriptorSchema),
});
/**
 * A user's domain-wide ban, persisted in KV under `[domain, "bans", userId]` —
 * the ban applies across every room on the domain and is enforced purely by
 * `userId`. `bannedUntil` is an absolute epoch-ms instant; the entry is also
 * given a matching KV TTL so it self-expires.
 */
export const BanEntrySchema = z.object({
    userId: z.string().min(1).max(256),
    userName: z.string().max(256),
    bannedUntil: z.number(),
});
/**
 * `POST /v1/rooms/kick` body. `roomId` is optional — it identifies the live room
 * to evict from (the target for future live eviction) and is omitted by
 * room-less callers such as an admin "ban by userId". `banDurationSeconds` is
 * optional too: when omitted the user is kicked only (no ban, may rejoin
 * immediately); when present the user is banned domain-wide for that many
 * seconds (server converts to an absolute `bannedUntil`). Capped at ~10 years to
 * bound the TTL.
 */
export const KickUserRequestSchema = z.object({
    userId: z.string().min(1).max(256),
    userName: z.string().max(256),
    roomId: z.string().min(1).max(256).optional(),
    banDurationSeconds: z.number().int().positive().max(315_360_000).optional(),
});
/**
 * `POST /v1/rooms/bans/remove` body. Lifts a user's domain-wide ban early
 * (before its `bannedUntil`). Identified by the same `userId` the ban was
 * recorded under.
 */
export const RemoveBanRequestSchema = z.object({
    userId: z.string().min(1).max(256),
});
/** `GET /v1/rooms/bans` response. */
export const ListBansResponseSchema = z.object({
    bans: z.array(BanEntrySchema),
});
